import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {fetchDelivery} from "../../../../../reduxLib/deliveryLib";
import {fetchOrderSummaryById} from "../../../../../reduxLib/ordersLib";
import ViewAndModifyDeliveryModal from "./elements/viewAndModifyDeliveryModal";
import DeliveryInitiateModal from "./elements/deliveryInitiateModal";
import InvoiceModal from "./elements/invoiceModal";

function DeliveriesAd() {
    const dispatch = useDispatch();

    const [deliveryPage, setDeliveryPage] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);

    const [statusFilter, setStatusFilter] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const itemsPerPage = 10;

    useEffect(() => {
        dispatch(fetchDelivery()).then(async (action) => {
            if (!action.payload) return;

            const deliveries = action.payload;

            const deliveriesWithOwners = await Promise.all(
                deliveries.map(async (delivery) => {
                    const orderAction = await dispatch(
                        fetchOrderSummaryById(delivery.orderRelId)
                    );

                    return {
                        ...delivery,
                        invoiceId:
                            orderAction.payload?.invoiceID ?? null,
                        orderOwnerName:
                            orderAction.payload?.orderOwnerName || "N/A",

                        orderOwnerEmail:
                            orderAction.payload?.orderOwnerEmail || "N/A",

                        totalPrice:
                            orderAction.payload?.totalPrice ?? 0,
                    };
                })
            );

            setDeliveryPage(deliveriesWithOwners);
        });
    }, [dispatch]);

    /* -------------------------------- */
    /* FILTERING */
    /* -------------------------------- */

    const filteredDeliveries = deliveryPage.filter((delivery) => {
        const matchesStatus =
            statusFilter === "all" ||
            delivery.deliveryStatus?.toLowerCase() ===
            statusFilter.toLowerCase();

        const initiatedDate = delivery.deliveryInitated
            ? new Date(delivery.deliveryInitated)
            : null;

        const fromDate = dateFrom
            ? new Date(dateFrom)
            : null;

        const toDate = dateTo
            ? new Date(dateTo)
            : null;

        // include whole day
        if (toDate) {
            toDate.setHours(23, 59, 59, 999);
        }

        const matchesFrom =
            !fromDate ||
            (initiatedDate &&
                initiatedDate >= fromDate);

        const matchesTo =
            !toDate ||
            (initiatedDate &&
                initiatedDate <= toDate);

        return (
            matchesStatus &&
            matchesFrom &&
            matchesTo
        );
    });

    /* -------------------------------- */
    /* PAGINATION */
    /* -------------------------------- */

    const totalPages = Math.ceil(
        filteredDeliveries.length / itemsPerPage
    );

    const paginatedDeliveries =
        filteredDeliveries.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );

    /* -------------------------------- */
    /* RESET PAGE */
    /* -------------------------------- */

    useEffect(() => {
        setCurrentPage(1);
    }, [
        deliveryPage.length,
        statusFilter,
        dateFrom,
        dateTo,
    ]);

    const today = new Date();

    const isToday = (date) => {
        if (!date) return false;

        const d = new Date(date);

        return (
            d.getDate() === today.getDate() &&
            d.getMonth() === today.getMonth() &&
            d.getFullYear() === today.getFullYear()
        );
    };

    /* -------------------------------- */
    /* STATS */
    /* -------------------------------- */

    const pendingDeliveries = deliveryPage.filter(
        (d) =>
            d.deliveryStatus?.toLowerCase() === "pending"
    );

    const pendingToday = pendingDeliveries.filter(
        (d) => isToday(d.deliveryInitated)
    );

    const inTransitDeliveries = deliveryPage.filter(
        (d) =>
            d.deliveryStatus?.toLowerCase() ===
            "in_transit"
    );

    const arrivingToday = inTransitDeliveries.filter(
        (d) =>
            isToday(d.deliveryEstimationArival)
    );

    const totalDeliveries =
        deliveryPage.length;

    const NeedsAttentionDeliveries = deliveryPage.filter(
        (d) => !d.deliveryDispach
    ).length;

    /*MODALS*/
    const [selectedDelivery, setSelectedDelivery] = useState(null);

    const [showViewModal, setShowViewModal] = useState(false);
    const [showInitiateModal, setShowInitiateModal] = useState(false);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);

    return (
        <div id="delivery-content" className="p-8 bg-cream font-print">
            <div id="delivery-stats" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Pending */}
                <div className="bg-white p-6 rounded-lg shadow card-hover">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">
                                Pending Deliveries
                            </p>

                            <p className="text-2xl font-bold text-gray-800">
                                {pendingDeliveries.length}
                            </p>

                            <p className="text-orange-600 text-sm mt-1">
                                {pendingToday.length} initiated today
                            </p>
                        </div>

                        <i className="fa-solid fa-clock text-3xl text-orange-500"></i>
                    </div>
                </div>

                {/* In Transit */}
                <div className="bg-white p-6 rounded-lg shadow card-hover">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">
                                In Transit
                            </p>

                            <p className="text-2xl font-bold text-gray-800">
                                {inTransitDeliveries.length}
                            </p>

                            <p className="text-blue-600 text-sm mt-1">
                                {arrivingToday.length} arriving today
                            </p>
                        </div>

                        <i className="fa-solid fa-truck text-3xl text-blue-500"></i>
                    </div>
                </div>

                {/* Total */}
                <div className="bg-white p-6 rounded-lg shadow card-hover">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">
                                Total Deliveries
                            </p>

                            <p className="text-2xl font-bold text-gray-800">
                                {totalDeliveries}
                            </p>
                        </div>

                        <i className="fa-solid fa-map text-3xl text-red-500"></i>
                    </div>
                </div>

                {/*Needs Attention*/}
                <div className="bg-white p-6 rounded-lg shadow card-hover">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">
                                Not Despatched
                            </p>

                            <p className="text-2xl font-bold text-gray-800">
                                {NeedsAttentionDeliveries}
                            </p>
                            <p className="text-orange-600 text-sm mt-1">They need Attention</p>
                        </div>

                        <i className="fa-solid fa-exclamation-triangle text-3xl text-orange-500"></i>
                    </div>
                </div>


            </div>

            <div id="delivery-filters" className="bg-white rounded-lg shadow p-6 mb-8">
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium text-gray-700">Status:</label>
                        <select className="border rounded-lg px-3 py-2 text-sm"
                                value={statusFilter}
                                onChange={(e) =>
                                    setStatusFilter(e.target.value)
                                }>
                            <option value="all">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="in_transit">In Transit</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>

                    <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium text-gray-700">Date Range:</label>
                        <input type="date" className="border rounded-lg px-3 py-2 text-sm"
                               value={dateFrom}
                               onChange={(e) =>
                                   setDateFrom(e.target.value)
                               }/>
                        <span className="text-gray-400">to</span>
                        <input type="date" className="border rounded-lg px-3 py-2 text-sm"
                               value={dateTo}
                               onChange={(e) =>
                                   setDateTo(e.target.value)
                               }/>
                    </div>

                    <div className="flex-1"></div>

                    <div className="flex items-center space-x-2">
                        <div className="relative">
                            <input type="text" placeholder="Search deliveries..."
                                   className="border rounded-lg pl-10 pr-4 py-2 text-sm w-64"/>
                            <i className="fa-solid fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                        </div>
                        <button
                            className="bg-custom-green text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-opacity-90">
                            <i className="fa-solid fa-filter mr-2"></i>
                            Filter
                        </button>
                    </div>
                </div>
            </div>

            <div id="delivery-table" className="bg-white rounded-lg shadow overflow-hidden mb-8">
                <div className="px-6 py-4 border-b">
                    <h2 className="text-xl font-serif font-bold text-custom-green">Active Deliveries</h2>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order
                                ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Delivery Details</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Est.
                                Delivery
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedDeliveries.length > 0 ? (
                            paginatedDeliveries.map((delivery) => (
                                <tr
                                    key={delivery.deliveryID}
                                    className="hover:bg-gray-50"
                                >
                                    {/* Order ID */}
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            #{delivery.orderRelId}
                                        </div>

                                        <div className="text-sm text-gray-500">
                                            Delivery ID: {delivery.deliveryID}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            €{Number(delivery.totalPrice ?? 0).toFixed(2)}
                                        </div>
                                    </td>

                                    {/* Customer */}
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {delivery.orderOwnerName || "N/A"}
                                                </div>

                                                <div className="text-sm text-gray-500">
                                                    {delivery.orderOwnerEmail || "N/A"}
                                                </div>
                                            </div>
                                        </div>
                                    </td>

                                    {/* Address */}
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {delivery.address?.street || "N/A"}{" "}
                                            {delivery.address?.number || ""}
                                        </div>

                                        <div className="text-sm text-gray-500">
                                            {delivery.address?.city || "N/A"},{" "}
                                            {delivery.address?.postalCode || ""}
                                        </div>

                                        <div className="text-xs text-gray-400 mt-1">
                                            {delivery.address?.region || "N/A"} •{" "}
                                            {delivery.address?.country || "N/A"}
                                        </div>
                                    </td>

                                    {/* Delivery Number */}
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900 truncate max-w-[200px]">
                                            {delivery.delivery_Number || "N/A"}
                                        </div>

                                        <div className="text-sm text-gray-500">
                                            Tracking:{" "}
                                            {delivery.tracking_Number || "Pending"}
                                        </div>
                                    </td>

                                    {/* Status */}
                                    <td className="px-6 py-4 whitespace-nowrap">
                    <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            delivery.deliveryStatus === "pending"
                                ? "bg-orange-100 text-orange-700"
                                : delivery.deliveryStatus === "completed"
                                    ? "bg-green-100 text-green-700"
                                    : delivery.deliveryStatus === "cancelled"
                                        ? "bg-red-100 text-red-700"
                                        : "bg-blue-100 text-blue-700"
                        }`}
                    >
                        {delivery.deliveryStatus}
                    </span>
                                    </td>

                                    {/* Estimated Delivery */}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {delivery.deliveryEstimationArival ? (
                                            <>
                                                {new Date(
                                                    delivery.deliveryEstimationArival
                                                ).toLocaleDateString()}
                                                <br />

                                                <span className="text-gray-500">
                                {new Date(
                                    delivery.deliveryEstimationArival
                                ).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })}
                            </span>
                                            </>
                                        ) : (
                                            <span className="text-gray-400">
                            No ETA
                        </span>
                                        )}
                                    </td>

                                    {/* Actions */}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                        {/* View & Modify */}
                                        <button
                                            onClick={() => {
                                                setSelectedDelivery(delivery);
                                                setShowViewModal(true);
                                            }}
                                            className="text-blue-600 hover:text-blue-900"
                                        >
                                            <i className="fa-solid fa-eye"></i>
                                        </button>

                                        {/* Delivery Initiate */}
                                        <button
                                            onClick={() => {
                                                setSelectedDelivery(delivery);
                                                setShowInitiateModal(true);
                                            }}
                                            className="text-custom-green hover:text-opacity-80"
                                        >
                                            <i className="fa-solid fa-envelope"></i>
                                        </button>

                                        {/* Invoice */}
                                        <button
                                            onClick={() => {
                                                setSelectedDelivery(delivery);
                                                setShowInvoiceModal(true);
                                            }}
                                            className="text-gray-600 hover:text-gray-900"
                                        >
                                            <i className="fa-solid fa-file-text"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td
                                    colSpan="7"
                                    className="text-center py-8 text-gray-500"
                                >
                                    No deliveries found
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center">
                    <div className="text-sm text-gray-700">
                        Showing{" "}
                        {filteredDeliveries.length === 0
                            ? 0
                            : (currentPage - 1) * itemsPerPage + 1}
                        {" "}to{" "}
                        {Math.min(currentPage * itemsPerPage, filteredDeliveries.length)}
                        {" "}of {filteredDeliveries.length} deliveries
                    </div>

                    <div className="flex space-x-2">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((prev) => prev - 1)}
                            className="px-3 py-1 border rounded text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>

                        {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                            (page) => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-3 py-1 border rounded text-sm ${
                                        currentPage === page
                                            ? "bg-custom-green text-white"
                                            : "text-gray-600 hover:bg-gray-100"
                                    }`}
                                >
                                    {page}
                                </button>
                            )
                        )}

                        <button
                            disabled={currentPage === totalPages || totalPages === 0}
                            onClick={() => setCurrentPage((prev) => prev + 1)}
                            className="px-3 py-1 border rounded text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
            {/* ViewAndModifyDeliveryModal */}
            <ViewAndModifyDeliveryModal
                isOpen={showViewModal}
                onClose={() => setShowViewModal(false)}
                delivery={selectedDelivery}
                invoiceId={selectedDelivery?.invoiceId}
            />

            {/* DeliveryInitiateModal */}
            <DeliveryInitiateModal
                isOpen={showInitiateModal}
                onClose={() => setShowInitiateModal(false)}
                delivery={selectedDelivery}
                invoiceId={selectedDelivery?.invoiceId}
            />

            {/* InvoiceModal */}
            <InvoiceModal
                isOpen={showInvoiceModal}
                onClose={() => setShowInvoiceModal(false)}
                delivery={selectedDelivery}
                invoiceId={selectedDelivery?.invoiceId}
            />


        </div>

    );

}

export default DeliveriesAd;